/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import './style/createPdfTemplate.scss';

import React, {MouseEventHandler, useEffect, useMemo, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {Loader, PDFElementParams, PdfFont, PDFSubmitHandler, PDFTemplateCreator} from '@dvrd/dvr-controls';

import {getMenuWidth} from '../../../util/menuUtil';

import {getCustomVariables} from '../../../util/pdfUtil';

import {BolmateButton} from '../../controls';

import InfoBanner from '../../misc/infoBanner';

interface Props {
    onPreview: PDFSubmitHandler;
    onSubmit: PDFSubmitHandler;
    onClickProceed: PDFSubmitHandler | null;
    onChangeFont: (font: PdfFont, callback?: VoidFunction) => void;
    onClickImport: MouseEventHandler;
    elements: PDFElementParams<any, any>[];
    resetElements: PDFElementParams<any, any>[];
    loading: boolean;
    values: Record<string, string>;
    mainFont: PdfFont;
    version: number;
    hasAccounts: boolean;
}

export default function PdfTemplateEditor(props: Props) {
    const {
        onClickProceed,
        onChangeFont,
        elements,
        onPreview,
        loading,
        onSubmit,
        resetElements,
        values,
        mainFont,
        version,
        hasAccounts,
        onClickImport
    } = props;
    const {t} = useTranslation();
    const maxWidth = getMenuWidth();
    const customVariables = useMemo(() => getCustomVariables(), [t]);
    const creatorRef = useRef<PDFTemplateCreator>(null);
    const lastVersion = useRef<number>(-1);

    useEffect(() => {
        if (lastVersion.current !== version) {
            lastVersion.current = version;
            if (creatorRef.current) creatorRef.current.onReset();
        }
    }, [version]);

    return (
        <>
            <InfoBanner
                featureKey='invoice_layout'
                variant='info'
                title={t('pdfTemplate.editor.bannerTitle', 'Factuurlayout')}
                showForNewUsersOnly={true}
            >
                {t(
                    'pdfTemplate.editor.bannerText',
                    'Ontwerp je factuurlayout zodat elke factuur in je eigen huisstijl verstuurd wordt.'
                )}
            </InfoBanner>
            <div className='create-pdf-template'>
                <Loader active={loading} position='fixed' />
                <PDFTemplateCreator
                    onSubmit={onSubmit}
                    defaultItems={elements}
                    customVariables={customVariables}
                    resetItems={resetElements}
                    onClickProceed={onClickProceed ?? undefined}
                    maxWidth={`calc(100vw - ${maxWidth}px)`}
                    values={values}
                    onPreview={onPreview}
                    onChangeFont={onChangeFont}
                    mainFont={mainFont}
                    disableElements={['invoiceTable']}
                    headerButtons={
                        hasAccounts
                            ? [
                                  <BolmateButton
                                      onClick={onClickImport}
                                      label={t('pdfTemplate.editor.importFrom', 'Ophalen uit...')}
                                  />
                              ]
                            : undefined
                    }
                    ref={creatorRef}
                    supportMultiPage
                    singleImage
                    tableHeaderLabels={[
                        t('pdfTemplate.editor.productName', 'Productnaam'),
                        t('pdfTemplate.editor.quantity', 'Aantal'),
                        t('pdfTemplate.editor.price', 'Prijs'),
                        t('pdfTemplate.editor.vat', 'BTW'),
                        t('pdfTemplate.editor.subtotal', 'Subtotaal excl. BTW')
                    ]}
                />
            </div>
        </>
    );
}
